import axios from 'axios';
import { useAuthStore } from '../../store/auth.store';
import NProgress from 'nprogress';

let activeRequests = 0;

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api';

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor: Attach bearer token if it exists and trigger NProgress
apiClient.interceptors.request.use(
  (config) => {
    if (activeRequests === 0) {
      NProgress.start();
    }
    activeRequests++;

    const token = useAuthStore.getState().accessToken;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    activeRequests = Math.max(0, activeRequests - 1);
    if (activeRequests === 0) {
      NProgress.done();
    }
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue: any[] = [];
let latestRefreshToken: string | null = null;

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// A request retried after a silent refresh may carry a `refreshToken` in its body
// (e.g. /auth/logout) that was captured before rotation — patch it to the freshly
// rotated value so the retry doesn't revoke a token that's already been superseded.
const patchStaleRefreshTokenBody = (config: any, newRefreshToken: string) => {
  if (!config?.data) return;
  try {
    const isString = typeof config.data === 'string';
    const parsed = isString ? JSON.parse(config.data) : config.data;
    if (parsed && typeof parsed === 'object' && 'refreshToken' in parsed) {
      parsed.refreshToken = newRefreshToken;
      config.data = isString ? JSON.stringify(parsed) : parsed;
    }
  } catch {
    // Not a JSON body; nothing to patch
  }
};

// Response Interceptor: Catch 401s and attempt silent refresh
apiClient.interceptors.response.use(
  (response) => {
    activeRequests = Math.max(0, activeRequests - 1);
    if (activeRequests === 0) {
      NProgress.done();
    }
    return response;
  },
  async (error) => {
    activeRequests = Math.max(0, activeRequests - 1);
    if (activeRequests === 0) {
      NProgress.done();
    }
    const originalRequest = error.config;

    // Prevent infinite loop if auth/login or auth/refresh fails
    if (
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/refresh')
    ) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            if (latestRefreshToken) {
              patchStaleRefreshTokenBody(originalRequest, latestRefreshToken);
            }
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = useAuthStore.getState().refreshToken;

      if (!refreshToken) {
        useAuthStore.getState().clearAuth();
        if (typeof window !== 'undefined') {
          document.cookie = 'bb_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
          window.location.href = `/login?from=${encodeURIComponent(window.location.pathname)}`;
        }
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_URL}/auth/refresh`, {
          refreshToken,
        });

        const resData = response.data;
        const authData = resData.success ? resData.data : resData;

        const newAccessToken = authData.accessToken;
        const newRefreshToken = authData.refreshToken;

        useAuthStore.getState().updateAccessToken(newAccessToken);
        if (newRefreshToken) {
          useAuthStore.setState({ refreshToken: newRefreshToken });
          latestRefreshToken = newRefreshToken;
        }

        if (typeof window !== 'undefined') {
          const rememberMe = useAuthStore.getState().rememberMe;
          const maxAge = rememberMe ? `; max-age=${60 * 60 * 24 * 30}` : '';
          document.cookie = `bb_token=${newAccessToken}; path=/${maxAge}`;
        }

        processQueue(null, newAccessToken);
        isRefreshing = false;

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        if (newRefreshToken) {
          patchStaleRefreshTokenBody(originalRequest, newRefreshToken);
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        useAuthStore.getState().clearAuth();
        if (typeof window !== 'undefined') {
          document.cookie = 'bb_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
          window.location.href = `/login?from=${encodeURIComponent(window.location.pathname)}`;
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);
export default apiClient;
